// onboarding.share.* (ops plan 2026-10-17-launchpad-share; parent 2026-10-17-launchpad-menu-build §4.6, Decided): the
// rep shows the client the menu live from Launchpad through a link, and the client may approve it there.
//   - Launchpad starts a share for one location and one draft (`onboarding.share.start`). The cloud keeps a record and
//     only the SHA-256 of the link's secret. The address `/watch#<secret>` holds nothing else, and the secret sits in
//     the fragment, so no server log, proxy or Referer carries it. It is in the first answer only (a D7 secret output).
//   - The client's page swaps the secret (in a POST body) for a short token of the `review_link` audience, narrowed to
//     the share (`POST /v1/auth/share/session`). It reaches nothing but the share's own frames and the approval.
//   - A share lives SHARE_RULES.linkDays (core), until the rep ends it (`onboarding.share.stop`), a newer share of the
//     same rep replaces it, or the menu is sent. Frames are never stored (the share relay, a later slice).
//   - Claude never shares: neither command is an agent tool (parent plan 4.1).
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand } from '../../define/operations.ts';
import { secret } from '../../define/secret.ts';
import { IsoInstant, LocationId } from '../../primitives/codecs.ts';
import { MenuDigest } from '../catalog/menu-bundle.ts';
import { ReviewLinkNumberNotAllowed, ReviewLinkTextFailed } from './session.ts';
import { SupportCallNotFound } from './support.ts';

const SHARE_CLOUD = "A share link is served from the cloud, which relays the rep's screen to the client and checks the link on every call.";

/** One share: minted by the cloud (UUIDv7) at the start, never chosen by the caller. A retried start replays its answer. */
export const ShareId = z.uuid({ version: 'v7' }).meta({ id: 'ShareId' });

/** How a share ended (core ShareEnd). */
export const ShareEnd = z.enum(['ended_by_rep', 'replaced', 'sent', 'expired']).meta({ id: 'ShareEnd' });
export type ShareEnd = z.output<typeof ShareEnd>;

/** The link's token life and the frames' limits. The share's own rules are core's SHARE_RULES (one place). */
export const SHARE = {
  /** A link's token lasts this long; the page swaps the link again before it runs out. */
  tokenSeconds: 1800,
  /** A frame's encoded image at most (base64 of a JPEG), as See the screen's. */
  maxFrameBytes: 262_144,
  /** At most this many frames a second from Launchpad; more are refused. */
  maxFramesPerSecond: 4,
  /** The client's page waits at most this long for a newer frame. */
  maxWaitMs: 10_000,
  /** At most this many reads a second from one share's tokens together, and from one address across shares. */
  maxReadsPerSecond: 20,
  maxReadsPerAddressPerSecond: 10,
} as const;

/** The raw routes the frames travel on (cloud only; invariant A14's SHARE_ROUTES allow-list). */
export const SHARE_PATHS = {
  /** Launchpad posts one frame of the rep's menu area (the rep's staff session). */
  frames: '/v1/share/frames',
  /** The client's page reads the newest frame (the share link's token). */
  frame: '/v1/share/frame',
} as const;

/** A menu fingerprint (LP1's menuApprovalFingerprint). */
const Fingerprint = z.string().regex(/^sha256:[0-9a-f]{64}$/);

/**
 * What Launchpad posts: a picture of the menu area (the rep's pointer and click ripples drawn in), or `paused` when the
 * rep pressed Stop sharing, or `away` while a screen not meant for the client shows. Every post names the menu on
 * screen; `seq` rises by one each post.
 */
export const ShareFrameUpload = z.strictObject({
  shareId: ShareId,
  seq: z.int().positive(),
  capturedAt: IsoInstant,
  fingerprint: Fingerprint,
  frame: z.discriminatedUnion('kind', [
    z.strictObject({
      kind: z.literal('image'),
      width: z.int().min(1).max(4096),
      height: z.int().min(1).max(4096),
      /** The JPEG, base64. */
      jpeg: z.string().min(4).max(SHARE.maxFrameBytes).regex(/^[A-Za-z0-9+/]+={0,2}$/),
    }),
    z.strictObject({ kind: z.literal('paused') }),
    z.strictObject({ kind: z.literal('away') }),
  ]),
}).meta({ id: 'ShareFrameUpload' });
export type ShareFrameUpload = z.output<typeof ShareFrameUpload>;

/** The cloud's answer to Launchpad: keep sending (no sooner than `nextAfterMs`), or stop (the share is over). */
export const ShareFrameAck = z.strictObject({
  continue: z.boolean(),
  nextAfterMs: z.int().nonnegative(),
  ended: ShareEnd.nullable(),
  /** The menu on screen has been approved through the link (its key taken). */
  approved: z.boolean(),
  /**
   * As many shares are open as the cloud allows: nothing was shown. Launchpad says "Too many shares are open right now.
   * Try again in a few minutes." and posts again after `nextAfterMs`.
   */
  busy: z.literal(true).optional(),
}).meta({ id: 'ShareFrameAck' });
export type ShareFrameAck = z.output<typeof ShareFrameAck>;

/**
 * What the client's page reads: the share's state, the newest picture newer than the one it has, who is sharing and for
 * which store, and, while the rep shares a menu not yet approved, that menu's approval key (single use; LP1's
 * recordApproval takes it). Nothing staff-internal: no ids but the share's, no draft, no staff ids.
 */
export const ShareFrameView = z.strictObject({
  state: z.enum(['waiting', 'live', 'paused', 'ended']),
  ended: ShareEnd.nullable(),
  expiresAt: IsoInstant,
  /** The rep's first name, as Launchpad gave it at the start ("Sam"), or null. */
  repFirstName: z.string().nullable(),
  /** The store's own name. */
  locationName: z.string(),
  /** The store's time zone (IANA), so the page says "at 3:42 pm" in the store's own time. */
  locationTimeZone: z.string().optional(),
  frame: z.strictObject({
    seq: z.int().positive(),
    capturedAt: IsoInstant,
    width: z.int(),
    height: z.int(),
    jpeg: z.string(),
  }).nullable(),
  approval: z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('open'), fingerprint: Fingerprint, key: z.string().regex(/^[A-Za-z0-9_-]{22}$/) }),
    z.strictObject({ state: z.literal('approved'), fingerprint: Fingerprint }),
  ]).nullable(),
}).meta({ id: 'ShareFrameView' });
export type ShareFrameView = z.output<typeof ShareFrameView>;

/** No share has this id at this location. */
export const ShareNotFound = defineError({
  code: 'onboarding.share_not_found', httpStatus: 404, message: 'errors.onboarding.share_not_found', retryable: false,
  details: z.object({ shareId: ShareId }),
});

/** As many shares are open across the cloud as it allows (each can hold a frame in memory). */
export const ShareBusy = defineError({
  code: 'onboarding.share_busy', httpStatus: 429, message: 'errors.onboarding.share_busy', retryable: true,
  details: z.object({ open: z.int(), limit: z.int() }),
});

export const StartShare = defineCommand({
  name: 'onboarding.share.start',
  summary:
    "Start sharing Launchpad's menu with the client through a link: it lasts seven days, shows only the rep's shared menu while they share, can approve only that menu, and its address is in the answer once. The rep's earlier open share ends.",
  input: z.object({
    locationId: LocationId,
    /**
     * The draft this share shows, when Launchpad has one (its own id). Optional since Launchpad builds menus directly in
     * the location's cloud catalog (Andrew, 2026-10-17): the share then names no draft.
     */
    draftId: z.uuid().optional(),
    /** The menu on screen now: LP1's menuApprovalFingerprint (the menu and its layout keys). Frames carry it on. */
    fingerprint: MenuDigest,
    /** Text the link to this number (E.164) as it opens: only a number this cloud may text. Personal data, never stored or logged. */
    sendTo: z.string().regex(/^\+[1-9]\d{6,14}$/).optional(),
    /** The rep's first name for the client's page ("Sam is showing you your menu"). Shown to the client only. */
    repFirstName: z.string().trim().min(1).max(40).optional(),
    /** The support call the share happens on, which must be matched to this location. */
    callId: z.uuid().optional(),
  }),
  output: z.object({
    shareId: ShareId,
    texted: z.boolean().optional(),
    /** The address to send (D7 secret: the first answer only; a retry's replay leaves it out). */
    url: secret(z.string()).optional(),
    expiresAt: IsoInstant,
  }),
  permission: 'onboarding.menu.manage',
  risk: 'medium',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SHARE_CLOUD,
  emits: [],
  errors: [ShareBusy, ReviewLinkNumberNotAllowed, ReviewLinkTextFailed, SupportCallNotFound],
  // A dry run checks and stores and texts nothing.
  dryRun: true,
  // A person's click in Launchpad shares; Claude never does (parent plan 4.1).
  agent: { expose: false },
});

export const StopShare = defineCommand({
  name: 'onboarding.share.stop',
  summary: "End a share's link: the client's page and every token given out from it stop working at once.",
  input: z.object({ shareId: ShareId, locationId: LocationId }),
  output: z.object({ shareId: ShareId, ended: ShareEnd }),
  permission: 'onboarding.menu.manage',
  risk: 'low',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SHARE_CLOUD,
  emits: [],
  errors: [ShareNotFound],
  dryRun: false,
  agent: { expose: false },
});

export const shareCommands = [StartShare, StopShare] as const;
